'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Check, Minus, Star, Sparkles, ArrowRight, ChevronDown, ChevronUp, Play } from 'lucide-react';
import type { ProductVariant, VariantComparison } from '@/lib/data/software';

interface ComparisonFeature {
  name: string;
  category?: string;
  values: Record<string, boolean | string>;
}

interface VariantComparisonTableProps {
  variants: ProductVariant[];
  comparison: VariantComparison | null;
  productName: string;
  productSlug: string;
  className?: string;
}

/**
 * Parse features JSON from variant
 */
function parseVariantFeatures(featuresJson: string | null): string[] {
  if (!featuresJson) return [];
  try {
    const parsed = JSON.parse(featuresJson);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Get recommended variant index (middle-high tier usually best value)
 */
function getRecommendedIndex(variants: ProductVariant[]): number {
  if (variants.length <= 2) return variants.length - 1;
  if (variants.length === 3) return 1; // Middle
  return Math.floor(variants.length / 2); // Middle-ish for larger sets
}

/**
 * VariantCard - Individual variant display for card view
 */
function VariantCard({
  variant,
  isRecommended,
  productSlug,
  features,
}: {
  variant: ProductVariant;
  isRecommended: boolean;
  productSlug: string;
  features: string[];
}) {
  const contactUrl = `/contact?source=software-compare&product=${productSlug}&variant=${encodeURIComponent(variant.displayName)}`;

  return (
    <div
      className={`relative flex flex-col rounded-2xl border-2 p-6 h-full ${
        isRecommended
          ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/40/50 shadow-lg'
          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
      }`}
    >
      {/* Recommended Badge */}
      {isRecommended && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-xs font-semibold rounded-full">
            <Sparkles className="w-3 h-3" />
            Most Popular
          </span>
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          {variant.displayName}
        </h3>
        {variant.description && (
          <p className="text-sm text-gray-600 dark:text-gray-400">{variant.description}</p>
        )}
        {variant.videoUrl && (
          <a
            href={variant.videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
          >
            <Play className="w-3.5 h-3.5" />
            Watch Demo
          </a>
        )}
      </div>

      {/* Divider */}
      <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700" />

      {/* Features List */}
      {features.length > 0 && (
        <div className="flex-1 mb-6">
          <ul className="space-y-3">
            {features.slice(0, 6).map((feature, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
              </li>
            ))}
            {features.length > 6 && (
              <li className="text-sm text-gray-500 dark:text-gray-400 pl-7">
                +{features.length - 6} more features
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Ideal For */}
      {variant.idealFor && (
        <div className="mb-6 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
          <div className="flex items-center gap-2 text-sm">
            <Star className="w-4 h-4 text-amber-500" />
            <span className="font-medium text-gray-700 dark:text-gray-300">Best for:</span>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{variant.idealFor}</p>
        </div>
      )}

      {/* CTA */}
      <div className="mt-auto">
        <Link
          href={contactUrl}
          className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition-colors ${
            isRecommended
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-900 text-white hover:bg-gray-800'
          }`}
        >
          Request Quote
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

/**
 * ComparisonTable - Horizontal scrollable feature comparison
 */
function ComparisonTable({
  variants,
  features,
  recommendedIndex,
}: {
  variants: ProductVariant[];
  features: ComparisonFeature[];
  recommendedIndex: number;
}) {
  // Group features by category
  const groupedFeatures: Record<string, ComparisonFeature[]> = {};
  features.forEach((feature) => {
    const category = feature.category || 'Features';
    if (!groupedFeatures[category]) {
      groupedFeatures[category] = [];
    }
    groupedFeatures[category].push(feature);
  });

  return (
    <div className="overflow-x-auto -mx-4 sm:mx-0" tabIndex={0} role="region" aria-label="Variant comparison table">
      <div className="inline-block min-w-full align-middle">
        <table className="min-w-full">
          {/* Sticky Header */}
          <thead className="sticky top-0 z-10">
            <tr className="bg-gray-900">
              <th className="sticky left-0 z-20 bg-gray-900 text-left p-4 text-white font-semibold min-w-[200px]">
                Feature
              </th>
              {variants.map((variant, idx) => (
                <th
                  key={variant.id}
                  className={`p-4 text-center min-w-[160px] ${
                    idx === recommendedIndex
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-900 text-white'
                  }`}
                >
                  <div className="font-semibold">{variant.displayName}</div>
                  {idx === recommendedIndex && (
                    <div className="text-xs text-blue-200 mt-1">Recommended</div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(groupedFeatures).map(([category, categoryFeatures]) => (
              <>
                {/* Category Header */}
                <tr key={`cat-${category}`} className="bg-gray-100 dark:bg-gray-800">
                  <td
                    colSpan={variants.length + 1}
                    className="p-3 font-semibold text-gray-700 dark:text-gray-300 text-sm uppercase tracking-wide"
                  >
                    {category}
                  </td>
                </tr>
                {/* Feature Rows */}
                {categoryFeatures.map((feature, idx) => (
                  <tr
                    key={`${category}-${idx}`}
                    className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-800'}
                  >
                    <td className="sticky left-0 bg-inherit p-4 text-gray-700 dark:text-gray-300 font-medium border-r border-gray-100 dark:border-gray-800">
                      {feature.name}
                    </td>
                    {variants.map((variant, vIdx) => {
                      const value =
                        feature.values[variant.id] ??
                        feature.values[variant.displayName];
                      return (
                        <td
                          key={variant.id}
                          className={`p-4 text-center border-r border-gray-100 dark:border-gray-800 last:border-r-0 ${
                            vIdx === recommendedIndex ? 'bg-blue-50 dark:bg-blue-950/40/50' : ''
                          }`}
                        >
                          {typeof value === 'boolean' ? (
                            value ? (
                              <Check className="w-5 h-5 text-green-600 dark:text-green-400 mx-auto" />
                            ) : (
                              <Minus className="w-5 h-5 text-gray-300 mx-auto" />
                            )
                          ) : value ? (
                            <span className="text-gray-700 dark:text-gray-300 text-sm">{value}</span>
                          ) : (
                            <Minus className="w-5 h-5 text-gray-300 mx-auto" />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </>
            ))}

            {/* Best For Row */}
            {variants.some((v) => v.idealFor) && (
              <tr className="bg-amber-50 dark:bg-amber-950/40">
                <td className="sticky left-0 bg-amber-50 dark:bg-amber-950/40 p-4 text-gray-700 dark:text-gray-300 font-medium border-r border-gray-100 dark:border-gray-800">
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-amber-500" />
                    Best For
                  </div>
                </td>
                {variants.map((variant, vIdx) => (
                  <td
                    key={variant.id}
                    className={`p-4 text-center text-sm border-r border-gray-100 dark:border-gray-800 last:border-r-0 ${
                      vIdx === recommendedIndex ? 'bg-amber-100 dark:bg-amber-900/40/50' : ''
                    }`}
                  >
                    {variant.idealFor || '-'}
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * MobileVariantAccordion - Accordion view for mobile
 */
function MobileVariantAccordion({
  variants,
  features,
  recommendedIndex,
  productSlug,
}: {
  variants: ProductVariant[];
  features: ComparisonFeature[];
  recommendedIndex: number;
  productSlug: string;
}) {
  const [openIndex, setOpenIndex] = useState<number>(recommendedIndex);

  return (
    <div className="space-y-3">
      {variants.map((variant, idx) => {
        const isOpen = openIndex === idx;
        const isRecommended = idx === recommendedIndex;
        const variantFeatures = parseVariantFeatures(variant.features);
        const contactUrl = `/contact?source=software-compare&product=${productSlug}&variant=${encodeURIComponent(variant.displayName)}`;

        return (
          <div
            key={variant.id}
            className={`rounded-xl border-2 overflow-hidden ${
              isRecommended ? 'border-blue-500' : 'border-gray-200 dark:border-gray-700'
            }`}
          >
            {/* Header */}
            <button
              onClick={() => setOpenIndex(isOpen ? -1 : idx)}
              className={`w-full p-4 flex items-center justify-between ${
                isRecommended ? 'bg-blue-50 dark:bg-blue-950/40' : 'bg-white dark:bg-gray-800'
              }`}
            >
              <div className="flex items-center gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {variant.displayName}
                    </span>
                    {isRecommended && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-600 text-white text-xs font-medium rounded-full">
                        <Sparkles className="w-3 h-3" />
                        Popular
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {isOpen ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {/* Content */}
            {isOpen && (
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                {variant.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{variant.description}</p>
                )}

                {variant.videoUrl && (
                  <a
                    href={variant.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                  >
                    <Play className="w-3.5 h-3.5" />
                    Watch Demo
                  </a>
                )}

                {/* Features from comparison matrix */}
                {features.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {features.map((feature, fIdx) => {
                      const value =
                        feature.values[variant.id] ??
                        feature.values[variant.displayName];
                      if (!value) return null;
                      return (
                        <div key={fIdx} className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span className="text-gray-700 dark:text-gray-300">
                            {feature.name}
                            {typeof value === 'string' && value !== 'true' && (
                              <span className="text-gray-500 dark:text-gray-400"> ({value})</span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Features from variant */}
                {variantFeatures.length > 0 && features.length === 0 && (
                  <div className="space-y-2 mb-4">
                    {variantFeatures.map((feature, fIdx) => (
                      <div key={fIdx} className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Ideal For */}
                {variant.idealFor && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-950/40 rounded-lg mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Star className="w-4 h-4 text-amber-500" />
                      <span className="font-medium text-gray-700 dark:text-gray-300">Best for:</span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{variant.idealFor}</p>
                  </div>
                )}

                {/* CTA */}
                <Link
                  href={contactUrl}
                  className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition-colors ${
                    isRecommended
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  }`}
                >
                  Request Quote
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Add-on Card for the add-ons section
 */
function AddonCard({
  variant,
  productSlug,
}: {
  variant: ProductVariant;
  productSlug: string;
}) {
  const contactUrl = `/contact?source=software-compare&product=${productSlug}&variant=${encodeURIComponent(variant.displayName)}`;

  return (
    <div className="flex flex-col rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 p-5">
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center px-2 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 text-xs font-medium rounded-full">
            Add-on
          </span>
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">{variant.displayName}</h3>
        </div>
        {variant.description && (
          <p className="text-sm text-gray-600 dark:text-gray-400">{variant.description}</p>
        )}
        {variant.videoUrl && (
          <a
            href={variant.videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
          >
            <Play className="w-3.5 h-3.5" />
            Watch Demo
          </a>
        )}
      </div>
      <div className="mt-auto">
        <Link
          href={contactUrl}
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 transition-colors"
        >
          Add to Quote
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

/**
 * Main VariantComparisonTable Component
 */
export default function VariantComparisonTable({
  variants,
  comparison,
  productName,
  productSlug,
  className = '',
}: VariantComparisonTableProps) {
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');

  if (!variants || variants.length < 2) {
    return null;
  }

  // Separate main plans from add-ons
  const mainPlans = variants.filter((v) => !v.isAddon);
  const addons = variants.filter((v) => v.isAddon);

  // Parse comparison matrix from JSON
  let features: ComparisonFeature[] = [];
  if (comparison?.comparisonMatrix) {
    try {
      features = JSON.parse(comparison.comparisonMatrix);
    } catch {
      // Invalid JSON, features stays empty
    }
  }

  const recommendedIndex = getRecommendedIndex(mainPlans);

  return (
    <section className={`py-8 ${className}`}>
      {/* Section Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3">
          Compare {productName} Plans
        </h2>
        {comparison?.introText && (
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">{comparison.introText}</p>
        )}
        {!comparison?.introText && (
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Choose the plan that best fits your organization&apos;s needs and budget.
          </p>
        )}
      </div>

      {/* View Toggle (Desktop only) */}
      {features.length > 0 && (
        <div className="hidden md:flex justify-center gap-2 mb-8">
          <button
            onClick={() => setViewMode('cards')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'cards'
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            Card View
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'table'
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            Table View
          </button>
        </div>
      )}

      {/* Desktop: Cards or Table (main plans only) */}
      <div className="hidden md:block">
        {viewMode === 'cards' || features.length === 0 ? (
          <div
            className={`grid gap-6 ${
              mainPlans.length === 2
                ? 'grid-cols-2 max-w-3xl mx-auto'
                : mainPlans.length === 3
                ? 'grid-cols-3 max-w-5xl mx-auto'
                : 'grid-cols-2 lg:grid-cols-4'
            }`}
          >
            {mainPlans.map((variant, idx) => (
              <VariantCard
                key={variant.id}
                variant={variant}
                isRecommended={idx === recommendedIndex}
                productSlug={productSlug}
                features={parseVariantFeatures(variant.features)}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <ComparisonTable
              variants={mainPlans}
              features={features}
              recommendedIndex={recommendedIndex}
            />
          </div>
        )}
      </div>

      {/* Mobile: Accordion (main plans only) */}
      <div className="md:hidden">
        <MobileVariantAccordion
          variants={mainPlans}
          features={features}
          recommendedIndex={recommendedIndex}
          productSlug={productSlug}
        />
      </div>

      {/* Add-ons Section */}
      {addons.length > 0 && (
        <div className="mt-12">
          <div className="text-center mb-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Available Add-ons</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Enhance any plan above with these optional modules.
            </p>
          </div>
          <div
            className={`grid gap-4 ${
              addons.length === 1
                ? 'max-w-md mx-auto'
                : addons.length === 2
                ? 'grid-cols-1 md:grid-cols-2 max-w-3xl mx-auto'
                : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto'
            }`}
          >
            {addons.map((addon) => (
              <AddonCard
                key={addon.id}
                variant={addon}
                productSlug={productSlug}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
